import React from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@hero/web-components';
import { defaultMapConfig, mapStyles } from '@hero/web-utils';
import { useMapState } from '@hero/web-hooks';
import {
  MapProvider,
  useMapContext,
  type MapContextProps,
  type MapContextValue,
} from '@hero/web-context';
import { MapSearch } from '../MapSearch';
import { LocationCards } from '../LocationCards';
import { PendingLocationMarker, LocationMarkers, CurrentLocationMarker } from '../Markers';
import { RoutePolyline } from '../Polyline';
import { FitAllButtonHandler } from './FitAllButtonHandler';
import { GoogleMapWithMarkerProps } from './types';
import { MapSaveButton } from './MapSaveButton';
import { LocationEditModal } from '../../../feature/SavedLocation/Modal/SavedLocationEditModal';

/**
 * Inner component that uses the map context
 */
function GoogleMapWithMarkerInner() {
  const ctx = useMapContext();
  const { t } = useTranslation();
  const props = ctx.props as MapContextProps;
  const saveState = ctx.saveLocationState as
    | NonNullable<MapContextValue['saveLocationState']>
    | undefined;

  // Memoize map options to prevent re-renders
  const mapOptions = React.useMemo(
    () => ({
      ...defaultMapConfig.options,
      styles: mapStyles,
      gestureHandling: 'greedy',
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      optimizeWaypoints: true,
      suppressMarkers: false,
    }),
    [],
  );

  const mapCenter = React.useMemo(() => {
    // Priority 1: Explicit center from props
    if (props.mapConfig?.center) {
      return props.mapConfig.center;
    }

    // Priority 2: Center on first location if locations exist
    if (ctx.locations.length > 0) {
      return ctx.locations[0].cords;
    }

    // Priority 3: Use user's current location if available
    if (ctx.currentLocation?.cords) {
      return ctx.currentLocation.cords;
    }

    // Priority 4: Fall back to default center
    return defaultMapConfig.center;
  }, [props.mapConfig?.center, ctx.locations, ctx.currentLocation]);

  // When center changes after mount (e.g. SavedLocationMap when saved location loads), pan so the view updates (library often uses center only on first render)
  React.useEffect(() => {
    if (ctx.map && mapCenter) {
      ctx.map.panTo(mapCenter);
    }
  }, [ctx.map, mapCenter]);

  const mapHeight = props.mapConfig?.height || '400px';

  // Early returns for loading/error states
  if (ctx.loadError) {
    const isApiKeyError =
      ctx.loadError.message?.includes('API key') || ctx.loadError.message?.includes('missing');

    const errorMessage = isApiKeyError
      ? t('map.apiKeyError', 'Google Maps API key issue. Please check your API key configuration.')
      : ctx.loadError.message || t('map.loadError', 'Failed to load map');

    return (
      <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
        <div className="text-center p-6 max-w-md">
          <p className="text-red-600 font-semibold mb-2">{t('map.error', 'Map Error')}</p>
          <p className="text-gray-700 text-sm">{errorMessage}</p>
          {isApiKeyError && (
            <p className="text-gray-500 text-xs mt-2">
              {t(
                'map.apiKeyHelp',
                'Please contact your administrator or check your environment configuration.',
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!ctx.isLoaded) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
        <Spinner />
        <span className="ms-2">{t('map.loading', 'Loading map...')}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${props.className || ''}`} style={{ height: mapHeight }}>
      <GoogleMap
        mapContainerClassName="w-full h-full rounded-lg"
        center={mapCenter}
        zoom={props.mapConfig?.zoom || 15}
        options={mapOptions}
        onLoad={ctx.onMapLoad}
        onClick={ctx.handleMapClick}>
        {/* Search – MapSearch uses api-hooks internally */}
        {ctx.showSearch && (
          <MapSearch
            ref={ctx.mapSearchRef}
            map={ctx.map}
            onLocationSelect={ctx.handleLocationSelect}
            showSearch={ctx.showSearch}
            searchMode={props.searchMode}
            client={props.clientConfig}
            isEditMode={ctx.editingIndex !== null}
            editingIndex={ctx.editingIndex}
            externalSearchTerm={
              ctx.editingLocation
                ? ctx.editingLocation.fullAddress ||
                  `${ctx.editingLocation.cords.lat}, ${ctx.editingLocation.cords.lng}`
                : undefined
            }
            onCurrentLocationChange={ctx.onCurrentLocationChange}
          />
        )}

        {/* Location Markers - renders all location markers */}
        <LocationMarkers />

        {/* Pending Location Marker - shown when editing */}
        <PendingLocationMarker />

        {/* Current Location Marker - blue dot for user's location */}
        <CurrentLocationMarker />

        {/* Route Polyline - renders route lines */}
        <RoutePolyline />
      </GoogleMap>

      {/* Location Cards - uses context directly */}
      {ctx.locations.length > 0 && <LocationCards position="top-left" />}

      {/* Fit All Button - uses context directly */}
      <FitAllButtonHandler />

      {/* Save Button - renders save/cancel buttons */}
      <MapSaveButton />

      {/* Custom bottom card - optional */}
      {props.renderBottomCard?.()}

      {/* Location Edit Modal – uses api-hooks internally; no API callables passed */}
      {ctx.mode !== 'view' && saveState?.isSaveLocationAvailable && saveState && (
        <LocationEditModal
          isOpen={saveState.isLocationModalOpen}
          onClose={saveState.closeLocationModal}
          onLocationSaved={saveState.handleLocationSaved}
          mode={saveState.locationModalSavedLocationId ? 'edit' : 'create'}
          savedLocationId={saveState.locationModalSavedLocationId}
          initialCoordinates={saveState.locationModalCoords}
          initialLocationUrl={saveState.locationModalLocationUrl}
          orgId={saveState.orgId}
        />
      )}
    </div>
  );
}

/**
 * Main GoogleMapWithMarker component
 * Integrates all map features: search, markers, directions, live tracking, etc.
 */
export function GoogleMapWithMarker(props: GoogleMapWithMarkerProps) {
  const mapWithMarker = useMapState(props);

  return (
    <MapProvider value={mapWithMarker}>
      <GoogleMapWithMarkerInner />
    </MapProvider>
  );
}
